import { z } from 'zod';
import { v1AgentUpdateStateSchema } from './agent-auto-update';
import { v1InventoryUserAlertSeveritySchema } from './inventory-users';
import { v1PaginationQuerySchema, v1PaginationSchema } from './pagination';
import { csvParam, v1SortOrderSchema } from './query';
import { v1ShadowAiCategoryNameSchema, v1ShadowAiCategorySchema, } from './shadow-ai';
/**
 * Endpoints (physical machines) for `/api/v1` (KIR-5411, KIR-5336).
 *
 * **Unpublished.** Registered with `schema.hide: true`, listed in
 * `UNPUBLISHED_ROUTES`, and answering the generic 404 while the
 * `shared_devices` flag is off for the calling organization. Nothing here is
 * frozen until the operations join `PUBLISHED_OPERATIONS`.
 *
 * An endpoint is one machine: every endpoint user's agent on it, grouped by
 * the hardware ID the agent reports. An agent that has not reported a hardware
 * ID yet is its own endpoint. Same handler and gate as the Inventory →
 * Endpoints screen: Owner, Manager or Support with `inventory:read`.
 */
/** Who an endpoint user seen on an endpoint is. */
export const v1EndpointUserIdentitySchema = z
    .enum(['member', 'discovered', 'unattributed', 'offboarded'])
    .meta({
    id: 'V1EndpointUserIdentity',
    title: 'Endpoint user identity',
    description: 'Who an endpoint user seen on an endpoint is. `member`: a person with a Kirin account in your organization. `discovered`: known by email only, without an account. `unattributed`: activity Kirin could not tie to anyone. `offboarded`: removed from your organization and still seen on the endpoint.',
});
/** An endpoint user named on an endpoint. */
export const v1EndpointUserRefSchema = z
    .object({
    id: z
        .string()
        .describe("The endpoint user's `id`, the same value `inventory-users` returns. Opaque."),
    name: z.string().nullable().describe('Display name, if known.'),
    email: z.string().nullable().describe('Email address, if known.'),
})
    .meta({
    id: 'V1EndpointUserRef',
    title: 'Endpoint user reference',
    description: 'An endpoint user seen on an endpoint.',
});
const endpointFields = {
    id: z
        .string()
        .describe('Opaque identifier for this endpoint. Stable while the endpoint keeps reporting the same hardware ID; safe to store and pass back. Do not parse it.'),
    name: z
        .string()
        .nullable()
        .describe('The hostname the most recently seen agent reported. Null when none was reported.'),
    osName: z
        .string()
        .nullable()
        .describe('The operating system the most recently seen agent reported.'),
    machineIds: z
        .array(z.string())
        .describe("Every endpoint user's `machineId` on this endpoint — the value `running-processes` and `agent-update-status` publish."),
    enrolled: z
        .boolean()
        .describe("Whether the endpoint is enrolled with your organization's MDM key."),
    agentVersion: z
        .string()
        .nullable()
        .describe('The agent version on the endpoint, from its most recently seen agent. Null before the first report.'),
    updateState: v1AgentUpdateStateSchema
        .nullable()
        .describe('Automatic-update state of that agent. Null before it first checks, or while automatic agent updates are not available to your organization.'),
    updateAvailable: z
        .boolean()
        .describe("Whether `agentVersion` is older than Knostic's latest production release. `false` when either version is unknown."),
    shared: z
        .boolean()
        .describe('Whether more than one endpoint user was seen on the endpoint in the last 30 days.'),
    endpointUserCount: z
        .number()
        .int()
        .describe('How many endpoint users were seen on the endpoint in the last 30 days.'),
    aiToolCount: z
        .number()
        .int()
        .nullable()
        .describe('How many distinct AI tools were detected on the endpoint. Null while Shadow AI is not enabled for your organization.'),
    aiToolNames: z
        .array(z.string())
        .nullable()
        .describe('Up to three of those AI tools by name, A to Z. Null while Shadow AI is not enabled for your organization.'),
    mcpServerCount: z
        .number()
        .int()
        .describe('How many distinct MCP servers are configured on the endpoint.'),
    alertCount: z
        .number()
        .int()
        .describe('How many distinct alerts were raised on the endpoint **in the last 7 days**. A rolling window, not a lifetime total.'),
    maxAlertSeverity: v1InventoryUserAlertSeveritySchema
        .nullable()
        .describe('The highest severity among those last-7-day alerts, or null when there were none.'),
    lastSeenAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When any agent on the endpoint last reported, ISO 8601 UTC.'),
};
/** One endpoint on the list. */
export const v1EndpointSchema = z.object(endpointFields).meta({
    id: 'V1Endpoint',
    title: 'Endpoint',
    description: 'One physical machine the Kirin agent runs on, with every endpoint user seen on it.',
    examples: [
        {
            id: 'hw-3b1f0c9e2d4a5b6c7d8e9f00112233445566778899aabbccddeeff0011223344',
            name: 'TRAINING-PC-7',
            osName: 'windows/amd64',
            machineIds: [
                'e85c251bb2c046fa459a6af3b7c1cee2',
                '0f2a9c4d1e3b5a7c9e1f3a5b7c9d1e3f',
            ],
            enrolled: true,
            agentVersion: '3.140.0',
            updateState: 'up-to-date',
            updateAvailable: false,
            shared: true,
            endpointUserCount: 2,
            aiToolCount: 3,
            aiToolNames: ['Claude Code', 'Cursor', 'GitHub Copilot'],
            mcpServerCount: 4,
            alertCount: 2,
            maxAlertSeverity: 'high',
            lastSeenAt: '2026-10-25T09:12:00.000Z',
        },
    ],
});
/** One endpoint user seen on an endpoint, with their alerts there. */
export const v1EndpointUserSchema = v1EndpointUserRefSchema
    .extend({
    identity: v1EndpointUserIdentitySchema,
    osAccount: z
        .string()
        .nullable()
        .describe('The OS account the endpoint user signs in with on this endpoint (e.g. `CORP\\\\alice`), when reported.'),
    guest: z
        .boolean()
        .describe("True for the endpoint's shared Guest or kiosk sessions: everyone who signs in that way is this one `unattributed` endpoint user. It can't be linked to an account."),
    machineId: z
        .string()
        .describe("This endpoint user's `machineId` on the endpoint."),
    firstSeenAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When their agent on the endpoint first reported.'),
    lastSeenAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When their agent on the endpoint last reported.'),
    alertCount: z
        .number()
        .int()
        .describe('How many distinct alerts this endpoint user raised on the endpoint in the last 7 days.'),
    maxAlertSeverity: v1InventoryUserAlertSeveritySchema
        .nullable()
        .describe('The highest severity among them, or null when none.'),
})
    .meta({
    id: 'V1EndpointUser',
    title: 'Endpoint user on an endpoint',
    description: 'An endpoint user seen on an endpoint in the last 30 days, and the alerts they raised there.',
});
/** One AI tool on an endpoint. */
export const v1EndpointAiToolSchema = z
    .object({
    toolId: z
        .string()
        .describe("The tool's identifier within Kirin's catalog."),
    name: z.string().describe('The tool as Kirin names it.'),
    category: v1ShadowAiCategorySchema,
    categoryName: v1ShadowAiCategoryNameSchema,
    lastSeenAt: z
        .string()
        .datetime()
        .nullable()
        .describe('The most recent detection on the endpoint, ISO 8601 UTC.'),
    seenWith: z
        .array(v1EndpointUserRefSchema)
        .describe('The endpoint users it was detected for on this endpoint.'),
})
    .meta({
    id: 'V1EndpointAiTool',
    title: 'AI tool on an endpoint',
    description: 'An AI tool detected on an endpoint, and who it was seen with.',
});
/** One endpoint with its people, AI tools and network identity. */
export const v1EndpointDetailSchema = z
    .object({
    ...endpointFields,
    ipAddresses: z
        .array(z.string())
        .describe('IP addresses the most recently seen agent reported.'),
    macAddresses: z
        .array(z.string())
        .describe('MAC addresses the most recently seen agent reported.'),
    firstSeenAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When any agent on the endpoint first reported.'),
    revokedAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When an administrator revoked the endpoint: its keys stopped working and it cannot enroll again until it is reset. Null when it is not revoked.'),
    endpointUsers: z
        .array(v1EndpointUserSchema)
        .describe('Endpoint users seen in the last 30 days, most recent first, each with their own alert count.'),
    aiTools: z
        .array(v1EndpointAiToolSchema)
        .describe('AI tools detected on the endpoint. Empty while Shadow AI is not enabled for your organization.'),
})
    .meta({
    id: 'V1EndpointDetail',
    title: 'Endpoint detail',
    description: 'One physical machine with the endpoint users seen on it, their alerts, and the AI tools detected there.',
});
/** Sortable fields, named for the public field they sort. */
export const v1EndpointSortFieldSchema = z
    .enum([
    'name',
    'agentVersion',
    'endpointUserCount',
    'alertCount',
    'lastSeenAt',
])
    .describe('Which field to sort by.');
/** Deliberately unnamed: `@fastify/swagger` cannot expand a `$ref` into parameters. */
export const v1EndpointListQuerySchema = v1PaginationQuerySchema.extend({
    endpoint: csvParam('Filter to endpoints with one of these names, endpoint `id`s or `machineId`s (exact, case-insensitive).'),
    agentVersion: csvParam('Filter by agent version.'),
    updateState: csvParam('Filter by automatic-update state, as `updateState` publishes it (`up-to-date`, `updating`, `pending-reboot`, `needs-attention`, `update-failed`, `pinned-by-mdm`, `not-available`, `off`).'),
    shared: z
        .enum(['true', 'false'])
        .optional()
        .describe('`true` for endpoints more than one endpoint user was seen on in the last 30 days, `false` for the rest.'),
    sortBy: v1EndpointSortFieldSchema.default('lastSeenAt'),
    sortOrder: v1SortOrderSchema.default('desc'),
});
export const v1EndpointListResponseSchema = z
    .object({
    data: z.array(v1EndpointSchema).describe('This page of endpoints.'),
    pagination: v1PaginationSchema,
})
    .meta({
    id: 'V1EndpointList',
    title: 'Endpoint list',
    description: 'A page of the physical machines the Kirin agent runs on.',
});
/**
 * KIR-5413: link an `unattributed` endpoint user to an account. Unpublished
 * with the rest of this file.
 */
export const v1EndpointUserLinkRequestSchema = z
    .object({
    accountId: z
        .string()
        .trim()
        .min(1)
        .max(255)
        .describe('The `id` of the account (from `accounts`) to link to. It must be an active account of your organization with an email address.'),
})
    .meta({
    id: 'V1EndpointUserLinkRequest',
    title: 'Link an endpoint user',
    description: 'Which account an `unattributed` endpoint user belongs to.',
});
export const v1EndpointUserLinkSchema = z
    .object({
    linkedInventoryUserId: z
        .string()
        .describe('The `unattributed` endpoint user that was linked. It no longer exists; its activity now belongs to `inventoryUserId`.'),
    inventoryUserId: z
        .string()
        .describe("The account's endpoint user `id`, the same value `inventory-users` returns. Opaque."),
    accountId: z
        .string()
        .describe('The account the endpoint user was linked to.'),
})
    .meta({
    id: 'V1EndpointUserLink',
    title: 'Endpoint user link',
    description: 'The result of linking an `unattributed` endpoint user to an account.',
});
/**
 * KIR-5658: revoke or reset an enrolled endpoint. Unpublished with the rest of
 * this file.
 */
export const v1EndpointRevocationSchema = z
    .object({
    id: z.string().describe('The endpoint `id`. Opaque.'),
    revokedAt: z
        .string()
        .datetime()
        .nullable()
        .describe('When the endpoint was revoked. Null after a reset: the endpoint can enroll again.'),
})
    .meta({
    id: 'V1EndpointRevocation',
    title: 'Endpoint revocation',
    description: 'Whether an enrolled endpoint is revoked.',
});
//# sourceMappingURL=endpoints.js.map